import { type UnitPerformanceLlmInsightResponse } from '../../../../api/customEndpoints/fetchUnitPerformanceScores';
import { type UnitInsightChatResponse } from '../../../../api/customEndpoints/getUnitInsightLlmChat';
import { ChatMessage } from './ChatMessage';
import { LlmInsightMimeTypes } from '../LlmFilesDropzone';

export function ChatMessageHistory({
  chat,
  insight,
  pendingUserMessage,
  streamingAssistantMessage,
}: {
  chat?: UnitInsightChatResponse;
  insight: UnitPerformanceLlmInsightResponse;
  pendingUserMessage?: {
    text: string;
    fileUrls: string[];
  };
  streamingAssistantMessage?: string;
}) {
  return (
    <div className="space-y-4">
      <OriginalInput insight={insight} />
      {chat
        ? chat.data.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))
        : null}
      <StreamingMessages
        pendingUserMessage={pendingUserMessage}
        streamingAssistantMessage={streamingAssistantMessage}
      />
    </div>
  );
}

function OriginalInput({
  insight,
}: {
  insight: UnitPerformanceLlmInsightResponse;
}) {
  return (
    <>
      <ChatMessage
        message={{
          id: 1,
          createdOn: insight.createdOn,
          contents: {
            role: 'user',
            version: 1,
            parts: insight.inputUserPromptFiles.map((fileUri) => ({
              fileData: {
                fileUri,
                // TODO: More mime types
                mimeType: fileUri.endsWith('.pdf')
                  ? 'application/pdf'
                  : 'application/octet-stream',
              },
            })),
          },
        }}
      />
      <ChatMessage
        message={{
          id: 2,
          createdOn: insight.createdOn,
          contents: {
            role: 'assistant',
            version: 1,
            parts: [
              {
                text: insight.content ?? '',
              },
            ],
          },
        }}
      />
    </>
  );
}

function StreamingMessages({
  pendingUserMessage,
  streamingAssistantMessage,
}: {
  pendingUserMessage?: {
    text: string;
    fileUrls: string[];
  };
  streamingAssistantMessage?: string;
}) {
  const mappedPendingUserMessage = pendingUserMessage
    ? {
        id: -1,
        createdOn: null,
        contents: {
          role: 'user' as const,
          version: 1 as const,
          parts: [
            ...pendingUserMessage.fileUrls.map((fileUri) => ({
              fileData: {
                fileUri,
                mimeType: getMimeTypeFromUrl(fileUri),
              },
            })),
            {
              text: pendingUserMessage.text,
            },
          ],
        },
      }
    : undefined;

  const mappedStreamingAssistantMessage = streamingAssistantMessage
    ? {
        id: -2,
        createdOn: null,
        contents: {
          role: 'assistant' as const,
          version: 1 as const,
          parts: [
            {
              text: streamingAssistantMessage,
            },
          ],
        },
      }
    : undefined;

  return (
    <>
      {mappedPendingUserMessage ? (
        <ChatMessage message={mappedPendingUserMessage} />
      ) : null}
      {mappedStreamingAssistantMessage ? (
        <ChatMessage message={mappedStreamingAssistantMessage} />
      ) : null}
    </>
  );
}

function getMimeTypeFromUrl(url: string): string {
  const entry = Object.entries(LlmInsightMimeTypes).find(([_, extensions]) =>
    extensions.some((ext) => url.endsWith(ext))
  );

  return entry ? entry[0] : 'application/octet-stream';
}
